import '../common/css/normalize.css';
import 'vite/modulepreload-polyfill';
import './sass/web.sass';
import '../common/sass/fonts.sass';
import Cookies from 'js-cookie';

declare global {
	interface Window {
		dataLayer: any[];
		LANG_CODE: string;
	}
}

const initGA = () => {
	window.dataLayer = window.dataLayer || [];

	// gtag.js only treats `arguments` objects as commands; a real array is ignored.
	function gtag(..._args: unknown[]) {
		// eslint-disable-next-line prefer-rest-params
		window.dataLayer.push(arguments);
	}

	gtag('js', new Date());
	gtag('config', 'G-3WHS7L428Y');

	// Load Google Analytics gtag.js dynamically after consent
	const gtagScript = document.createElement('script');
	gtagScript.async = true;
	gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-3WHS7L428Y';
	document.head.appendChild(gtagScript);

	// Load Zendesk widget
	const script = document.createElement('script');
	script.type = 'text/javascript';
	script.id = 'ze-snippet';
	script.async = true;
	script.src =
		'https://static.zdassets.com/ekr/snippet.js?key=eec51bab-e29f-400e-adea-535ab0aed0fd';
	document.getElementsByTagName('head')[0].appendChild(script);

	setTimeout(() => {
		// @ts-ignore
		zE('webWidget', 'setLocale', window.LANG_CODE);
	}, 3000);
};

/** `document.getElementById` but throw an error if its not found
 */
const forceGetElement = (id: string) => {
	const elem = document.getElementById(id);

	if (elem == null) {
		throw new Error(`Element with id ${id} not found`);
	}

	return elem;
};

const initWebsite = () => {
	const mobilenav = forceGetElement('mobile-menu-link');
	const langnav = forceGetElement('nav-language-link');
	const mobilemenu = forceGetElement('mobile-menu');
	const langmenu = forceGetElement('language-menu');

	mobilenav.addEventListener('click', () => {
		mobilemenu.classList.toggle('open');
		mobilenav.classList.toggle('open');
		mobilenav.setAttribute('aria-expanded', String(mobilemenu.classList.contains('open')));
	});

	mobilemenu.addEventListener('click', event => {
		if (!(event.target as HTMLElement).closest('a')) return;
		mobilemenu.classList.remove('open');
		mobilenav.classList.remove('open');
		mobilenav.setAttribute('aria-expanded', 'false');
	});

	langnav.addEventListener('click', () => {
		langmenu.classList.toggle('open');
		langnav.setAttribute('aria-expanded', String(langmenu.classList.contains('open')));
	});

	document.addEventListener('keydown', event => {
		if (event.key !== 'Escape') return;
		if (langmenu.classList.contains('open')) langnav.focus();
		else if (mobilemenu.classList.contains('open')) mobilenav.focus();
		[mobilemenu, mobilenav, langmenu].forEach(element => element.classList.remove('open'));
		[mobilenav, langnav].forEach(element => element.setAttribute('aria-expanded', 'false'));
	});

	document.querySelectorAll<HTMLAnchorElement>('#language-menu a').forEach(link => {
		const language = new URL(link.href).searchParams.get('lang');
		link.addEventListener('click', event => {
			event.preventDefault();
			const url = new URL(window.location.href);
			url.searchParams.set('lang', language || 'en');
			window.location.assign(url);
		});
	});

	const globe = document.getElementById('orna-globe');
	if (globe) {
		import('./globe').then(module => module.initGlobe(globe)).catch(() => {
			// The static Orna artwork remains visible if WebGL is unavailable.
			globe.classList.remove('is-ready');
			globe.querySelector('canvas')?.remove();
		});
	}
	if (document.getElementById('web-codex')) {
		import('./codex').then(module => module.mountCodex()).catch(() => {
			// Server-rendered links and content remain usable if the bundle fails.
		});
	}

	document
		.getElementById('cookie-banner-accept')
		?.addEventListener('click', (e: MouseEvent) => {
			e.preventDefault();
			Cookies.set('banneraccept', '1', { sameSite: 'Lax', expires: 365 });
			forceGetElement('cookie-banner').style.display = 'none';
			initGA();
		});
	document
		.getElementById('cookie-banner-decline')
		?.addEventListener('click', (e: MouseEvent) => {
			e.preventDefault();
			Cookies.set('banneraccept', '0', { sameSite: 'Lax' });
			forceGetElement('cookie-banner').style.display = 'none';
		});

	const bannerState = Cookies.get('banneraccept');

	const trailer = document.querySelector<HTMLAnchorElement>('[data-trailer]');
	trailer?.addEventListener('click', event => {
		if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
		const src = trailer.dataset.embed;
		if (!src) return;
		event.preventDefault();
		const player = document.createElement('iframe');
		player.src = src;
		player.title = trailer.dataset.title || '';
		player.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
		player.allowFullscreen = true;
		player.referrerPolicy = 'strict-origin-when-cross-origin';
		trailer.replaceWith(player);
		player.focus();
	});

	if (bannerState == undefined) {
		forceGetElement('cookie-banner').style.display = 'block';
	} else if (bannerState == '1') {
		initGA();
	}
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initWebsite);
else initWebsite();

export default {};
